/**
 * ImportActionWindow — what an `import` ACTION BUTTON opens.
 *
 * The Assistant's file import without the conversation: choose a file, check
 * how its columns land on the object's fields (matched by name first — the
 * same `suggest_mapping` the Assistant starts from — and fixable here), then
 * the SAME side-by-side ImportPreview the Assistant opens, where the records
 * are reviewed and saved through the object's own grid.
 *
 * A lookup field (a customer, an item) takes a code, a name, a description or
 * the other party's own code — the server resolves it. A column that only
 * HELPS find the record (a description beside a part number, a size) is
 * marked "helps find …": it is not imported itself, it sharpens the match, and
 * where the lookup column is blank it is what gets searched.
 *
 * Nothing here writes data. Staging, cleansing and the existence check run on
 * the server (the host's `record-import` endpoints, which apply the caller's
 * add/edit rights and read the button's mode from the model); the preview's
 * Save All goes through the engine's batch save.
 *
 * A DOCUMENT (PDF, photo, Word) first goes through a REVIEW step
 * (`ImportReview`): who sent it, what it is and the prompt that will read it,
 * recognised before anything is read, checked — and, with the right, edited —
 * then Processed. A host without the `read` call keeps the old order (the
 * document is read on upload).
 *
 * Shared by every host — the platform's Run view, the downloaded runtime and
 * J2EE — which each inject their `RecordImportTransport` and the object grid's
 * `CaptureRuntime`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, ArrowRight, FileUp, KeyRound, Loader2, Upload, UserRound, X } from 'lucide-react'
import type { ImportActionOutcome, ImportActionRequest } from '../../types'
import type { CaptureRuntime } from '../contract'
import { ImportPreview } from './ImportPreview'
import { ImportReview } from './ImportReview'
import { SenderProfiles } from './SenderProfiles'
import {
  importErrorInfo, isLockedUpload, isReview,
  type ImportMode, type ImportProfilesList, type ImportTargetForm, type RecordImportInspect,
  type ImportSenderPick, type RecordImportReadBody, type RecordImportReview, type RecordImportTarget,
  type RecordImportTransport,
} from './types'

const ACCEPT = '.csv,.tsv,.tab,.txt,.xlsx,.xlsm,.xls,.ods,.json,.pdf,.png,.jpg,.jpeg,.webp,.docx'
/** Read by the AI into rows rather than parsed (see doc_extract.py). */
const DOCUMENT_RE = /\.(pdf|png|jpe?g|webp|docx)$/i

const MODE_LABELS: Record<ImportMode, string> = {
  upsert: 'Add new and update existing',
  add: 'Add new records only',
  edit: 'Update existing records only',
}

type Step = 'pick' | 'review' | 'map' | 'preview' | 'profiles'

interface TargetOption { value: string; label: string; formNo: string; isHeader: boolean; lookup: boolean }

/** A map value that marks a column as a HINT for a lookup field, not a target. */
const HINT_PREFIX = 'hint:'

/** Header fields first, then each first-level child form's, as `<form_no>.<key>`.
 *  A third-level form is left out — the import cannot fill one. */
function targetOptions(forms: ImportTargetForm[]): { group: string; options: TargetOption[] }[] {
  if (!forms.length) return []
  const header = forms[0]
  const groups = [{
    group: header.title || 'Record',
    options: header.fields.map((f) => ({
      value: f.key, formNo: header.form_no, isHeader: true, lookup: !!f.lookup,
      label: `${f.label || f.key}${f.primary_key ? ' (key)' : ''}${f.required ? ' *' : ''}`,
    })),
  }]
  for (const form of forms.slice(1)) {
    if (form.role !== `child of form ${header.form_no}`) continue
    groups.push({
      group: `${form.title || `Form ${form.form_no}`} (rows)`,
      options: form.fields.map((f) => ({
        value: `${form.form_no}.${f.key}`, formNo: form.form_no, isHeader: false, lookup: !!f.lookup,
        label: `${f.label || f.key}${f.primary_key ? ' (key)' : ''}${f.required ? ' *' : ''}`,
      })),
    })
  }
  return groups
}

export function ImportActionWindow({ project, req, onClose, transport, captureRuntime, versionKey }: {
  project: string
  req: ImportActionRequest
  onClose: () => void
  /** The host's `/record-import/*` calls. */
  transport: RecordImportTransport
  /** The host's capture runtime for the object's grid (see ImportPreview). */
  captureRuntime: CaptureRuntime
  versionKey?: unknown
}) {
  const describe = transport.describeError ?? importErrorInfo
  const notify = captureRuntime.txn.notify
  const target: RecordImportTarget = useMemo(
    () => ({ project, objName: req.objName, action: req.actionName }),
    [project, req.objName, req.actionName],
  )
  const [step, setStep] = useState<Step>('pick')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<{ message: string; problems: string[] } | null>(null)
  const [info, setInfo] = useState<RecordImportInspect | null>(null)
  // A document's review step: recognised, not yet read.
  const [review, setReview] = useState<RecordImportReview | null>(null)
  const canReview = !!transport.read
  // "Recognise the sender and document type" — this person's saved choice for
  // the screen (server-side), else the screen's default. Off: a document is
  // read with the screen's general instructions only.
  const [detect, setDetect] = useState(true)
  const useReview = canReview && detect
  // The AI is looking (keywords did not recognise it).
  const [detecting, setDetecting] = useState(false)
  const [map, setMap] = useState<Record<string, string>>({})
  const [mode, setMode] = useState<ImportMode>(req.binding.mode || 'upsert')
  const [groupBy, setGroupBy] = useState<string>('')
  const [monthFirst, setMonthFirst] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  // Who sent the file (optional) — their profile is used to read a document.
  const [sender, setSender] = useState('')
  const [profilesInfo, setProfilesInfo] = useState<ImportProfilesList | null>(null)
  // A PDF that needs its password before it can be read.
  const [locked, setLocked] = useState<{ importId: string; filename: string; message: string } | null>(null)
  const [password, setPassword] = useState('')
  const [rememberPassword, setRememberPassword] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const closedRef = useRef(false)

  // Files finished with "Import another file": the window reports their sum
  // when it finally closes.
  const doneRef = useRef<ImportActionOutcome | null>(null)
  const announce = useCallback((outcome: ImportActionOutcome | null) => {
    if (outcome && outcome.saved > 0) {
      notify.success(`Imported ${outcome.saved} record${outcome.saved === 1 ? '' : 's'}`
        + (outcome.failed ? ` · ${outcome.failed} could not be saved` : '')
        + (outcome.pending ? ` · ${outcome.pending} not saved` : ''))
    }
  }, [notify])

  const finish = useCallback((outcome: ImportActionOutcome | null) => {
    if (closedRef.current) return
    closedRef.current = true
    announce(outcome)
    const total = doneRef.current && outcome ? addOutcomes(doneRef.current, outcome)
      : (doneRef.current || outcome)
    try {
      req.onClosed?.(total)
    } catch (err) {
      console.warn('[ImportActionWindow] onClosed handler failed', err)
    }
    onClose()
  }, [req, onClose, announce])

  // Whether this person may manage sender profiles (and the known senders,
  // offered as suggestions for the "From" field).
  useEffect(() => {
    if (!transport.listProfiles) return
    let cancelled = false
    transport.listProfiles(target)
      .then((res) => {
        if (cancelled) return
        setProfilesInfo(res)
        if (typeof res.detect === 'boolean') setDetect(res.detect)
      })
      .catch((e) => { console.info('[ImportActionWindow] sender profiles unavailable', e) })
    return () => { cancelled = true }
  }, [transport, target, step === 'profiles'])

  const senderOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const p of profilesInfo?.profiles || []) seen.set(p.sender_code, p.sender_display || p.sender_code)
    return [...seen.entries()]
  }, [profilesInfo])
  const senderLabel = useMemo(() => {
    const key = profilesInfo?.sender_field
    const f = key ? (info?.forms[0]?.fields || []).find((x) => x.key === key) : undefined
    return f?.label || (key ? key.replace(/_/g, ' ') : 'Sender')
  }, [profilesInfo, info])
  // No sender field on this screen ⇒ nothing to pick, profile or keep a
  // password under, so those controls are not offered.
  const hasSender = !!profilesInfo?.sender_field

  // Esc closes the pick / map steps (the preview handles its own Esc).
  useEffect(() => {
    if (step === 'preview') return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) finish(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [step, busy, finish])

  const adopt = useCallback((next: RecordImportInspect) => {
    setInfo(next)
    // A document's reading also says which columns only HELP find a record.
    const hinted: Record<string, string> = {}
    for (const [field, srcs] of Object.entries(next.suggested_hints || {})) {
      for (const src of srcs) hinted[src] = `${HINT_PREFIX}${field}`
    }
    setMap({ ...next.suggestion, ...hinted })
    if (next.lock_mode) setMode(next.mode)
    else if (!req.binding.mode) setMode(next.mode)
    setGroupBy(next.suggested_group_by || '')
    setError(null)
    setStep('map')
  }, [req.binding.mode])

  const showReview = useCallback((next: RecordImportReview) => {
    setReview(next)
    setInfo(null)
    setError(null)
    setStep('review')
  }, [])

  // The keywords-only answer came back. Recognised ⇒ the review step at once;
  // not ⇒ say "Detecting the format and sender…" while the AI looks.
  const detectSeq = useRef(0)
  const afterKeywords = useCallback(async (first: RecordImportReview) => {
    if (!first.needs_ai || !transport.review) { showReview(first); return }
    const mine = ++detectSeq.current
    setReview(first)
    setInfo(null)
    setStep('review')
    setDetecting(true)
    try {
      const full = await transport.review(target, first.import_id, { useAi: true })
      if (mine === detectSeq.current) showReview(full)
    } catch (e) {
      console.warn('[ImportActionWindow] detecting the format and sender failed', e)
      if (mine === detectSeq.current) {
        showReview(first)
        setError(describe(e, 'The format and sender could not be detected — pick them, or read it with the general instructions.'))
      }
    } finally {
      if (mine === detectSeq.current) setDetecting(false)
    }
  }, [transport, target, describe, showReview])

  const changeDetect = useCallback((on: boolean) => {
    setDetect(on)
    if (!transport.setPreferences) return
    transport.setPreferences(target, { detect: on }).catch((e) => {
      console.warn('[ImportActionWindow] the preference could not be saved', e)
      notify.error?.('Your choice could not be saved for next time.')
    })
  }, [transport, target, notify])

  const upload = useCallback(async (file: File) => {
    const isDoc = DOCUMENT_RE.test(file.name)
    const generic = isDoc && canReview && !detect
    setBusy(!isDoc ? 'Reading the file…'
      : useReview ? 'Looking for its sender and type…'
        : generic ? 'Reading the document with this screen’s instructions — this can take a minute…'
          : 'Reading the document — this can take a minute…')
    setError(null)
    try {
      const res = await transport.upload(target, file, {
        ...(sender.trim() && !generic ? { sender: sender.trim() } : {}),
        ...(useReview ? { review: true, useAi: false } : {}),
        ...(generic ? { generic: true } : {}),
      })
      if (isLockedUpload(res)) {
        setLocked({ importId: res.import_id, filename: res.filename, message: res.message })
        setPassword('')
        return
      }
      if (isReview(res)) void afterKeywords(res)
      else adopt(res)
    } catch (e) {
      console.warn('[ImportActionWindow] upload failed', e)
      setError(describe(e, 'The file could not be read.'))
    } finally {
      setBusy(null)
    }
  }, [target, adopt, transport, describe, sender, canReview, detect, useReview, afterKeywords])

  const recheck = useCallback(async (picks: { senderRef?: ImportSenderPick; docType?: string }) => {
    if (!review || !transport.review) return
    detectSeq.current++
    setDetecting(false)
    setBusy('Checking…')
    setError(null)
    try {
      showReview(await transport.review(target, review.import_id, { ...picks, useAi: true }))
    } catch (e) {
      console.warn('[ImportActionWindow] re-check failed', e)
      setError(describe(e, 'That choice could not be checked.'))
    } finally {
      setBusy(null)
    }
  }, [review, transport, target, describe, showReview])

  const processReview = useCallback(async (body: RecordImportReadBody) => {
    if (!review || !transport.read) return
    detectSeq.current++
    setDetecting(false)
    setBusy(body.generic
      ? 'Reading it with this screen’s instructions — this can take a minute…'
      : 'Reading the document — this can take a minute…')
    setError(null)
    try {
      const res = await transport.read(target, review.import_id, body)
      adopt(res)
      if (body.prompt !== undefined && body.savePrompt) notify.success('Prompt saved for next time')
    } catch (e) {
      console.warn('[ImportActionWindow] reading the reviewed document failed', e)
      setError(describe(e, 'The document could not be read.'))
    } finally {
      setBusy(null)
    }
  }, [review, transport, target, describe, adopt, notify])

  const unlock = useCallback(async () => {
    if (!locked || !password || !transport.unlock) return
    const generic = canReview && !detect
    setBusy(useReview ? 'Opening the PDF…' : 'Opening the PDF and reading it…')
    setError(null)
    try {
      const res = await transport.unlock(target, locked.importId, {
        password, remember: hasSender && rememberPassword,
        ...(sender.trim() && !generic ? { sender: sender.trim() } : {}),
        ...(useReview ? { review: true, useAi: false } : {}),
        ...(generic ? { generic: true } : {}),
      })
      setLocked(null)
      setPassword('')
      if (isReview(res)) void afterKeywords(res)
      else adopt(res)
      if (res.password?.remembered) notify.success('Password remembered for this sender')
      else if (rememberPassword && res.password?.note) notify.info?.(res.password.note)
    } catch (e) {
      console.warn('[ImportActionWindow] unlock failed', e)
      setError(describe(e, 'The PDF could not be opened.'))
    } finally {
      setBusy(null)
    }
  }, [locked, password, hasSender, rememberPassword, sender, target, transport, adopt, describe, notify,
    canReview, detect, useReview, afterKeywords])

  // The file is done (saved, or left): back to choosing the next one — the
  // same screen, the same sender switch, a clean slate.
  const importAnother = useCallback((outcome: ImportActionOutcome | null) => {
    announce(outcome)
    if (outcome) doneRef.current = doneRef.current ? addOutcomes(doneRef.current, outcome) : outcome
    detectSeq.current++
    setDetecting(false)
    setInfo(null)
    setReview(null)
    setLocked(null)
    setPassword('')
    setSender('')
    setMap({})
    setGroupBy('')
    setError(null)
    setBusy(null)
    setStep('pick')
  }, [announce])

  const changeSheet = useCallback(async (sheet: string) => {
    if (!info) return
    setBusy(`Reading sheet ${sheet}…`)
    try {
      adopt(await transport.inspect(target, info.import_id, sheet))
    } catch (e) {
      console.warn('[ImportActionWindow] sheet change failed', e)
      setError(describe(e, 'That sheet could not be read.'))
    } finally {
      setBusy(null)
    }
  }, [info, target, adopt, transport, describe])

  const groups = useMemo(() => targetOptions(info?.forms || []), [info])
  const optionByValue = useMemo(() => {
    const m = new Map<string, TargetOption>()
    groups.forEach((g) => g.options.forEach((o) => m.set(o.value, o)))
    return m
  }, [groups])

  const lookupOptions = useMemo(
    () => groups.flatMap((g) => g.options.filter((o) => o.lookup)),
    [groups],
  )
  const mapped = Object.entries(map).filter(([, t]) => t && !t.startsWith(HINT_PREFIX))
  const resolveHints = useMemo(() => {
    const out: Record<string, string[]> = {}
    for (const [src, t] of Object.entries(map)) {
      if (!t || !t.startsWith(HINT_PREFIX)) continue
      const field = t.slice(HINT_PREFIX.length)
      ;(out[field] ||= []).push(src)
    }
    return out
  }, [map])
  const childMapped = mapped.some(([, t]) => optionByValue.get(t)?.isHeader === false)
  const headerMapped = mapped.filter(([, t]) => optionByValue.get(t)?.isHeader)
  // One line per child row: lines repeating a header value belong to one
  // record. Default to the header's key when it is mapped.
  const headerKey = info?.forms[0]?.primary_key?.[0]
  const effectiveGroupBy = childMapped
    ? (groupBy || (headerKey && headerMapped.some(([, t]) => t === headerKey) ? headerKey : headerMapped[0]?.[1] || ''))
    : ''
  const duplicates = useMemo(() => {
    const seen = new Map<string, number>()
    mapped.forEach(([, t]) => seen.set(t, (seen.get(t) || 0) + 1))
    return new Set([...seen].filter(([, n]) => n > 1).map(([t]) => t))
  }, [mapped])

  const missingRequired = useMemo(() => {
    if (!info?.forms.length || mode === 'edit') return []
    const header = info.forms[0]
    const targets = new Set(mapped.map(([, t]) => t))
    return header.fields
      .filter((f) => f.required && !f.engine_filled && !targets.has(f.key))
      .map((f) => f.label || f.key)
  }, [info, mapped, mode])

  const keyHint = useMemo(() => {
    if (!info || mode === 'add') return null
    const best = info.key_candidates.find((c) => c.found > 0) || info.key_candidates[0]
    if (!best) return null
    const keyField = info.forms[0]?.fields.find((f) => f.key === headerKey)
    const keyLabel = keyField?.label || headerKey || 'the key'
    const mappedToKey = map[`#${best.column}`] === headerKey
    return {
      text: best.found > 0
        ? `Column "${best.header}" matches ${best.found} of ${best.sampled} existing ${keyLabel} value(s) checked.`
        : `Column "${best.header}" looks like it could hold ${keyLabel}.`,
      column: best.column, mappedToKey,
    }
  }, [info, mode, map, headerKey])

  const prepare = useCallback(async () => {
    if (!info) return
    setBusy('Checking every record against the application…')
    setError(null)
    try {
      await transport.prepare(target, info.import_id, {
        map: Object.fromEntries(mapped),
        mode, groupBy: effectiveGroupBy || null, monthFirst, sheet: info.sheet || null,
        ...(Object.keys(resolveHints).length ? { resolveHints } : {}),
      })
      setStep('preview')
    } catch (e) {
      console.warn('[ImportActionWindow] prepare failed', e)
      setError(describe(e, 'The import could not be prepared.'))
    } finally {
      setBusy(null)
    }
  }, [info, target, mapped, mode, effectiveGroupBy, monthFirst, resolveHints, transport, describe])

  const title = req.title || `Import into ${req.objName}`

  if (step === 'preview' && info) {
    return (
      <ImportPreview
        project={project}
        importId={info.import_id}
        objLabel={info.forms.find((f) => f.role === 'header')?.title || req.objName}
        fetchPreview={() => transport.preview(target, info.import_id)}
        fetchFile={transport.file ? (page) => transport.file!(target, info.import_id, page) : undefined}
        captureRuntime={captureRuntime}
        versionKey={versionKey}
        describeError={describe}
        onLearn={transport.learn
          ? (body) => transport.learn!(target, info.import_id, body)
          : undefined}
        onClose={(outcome) => finish(outcome)}
        onImportAnother={(outcome) => importAnother(outcome)}
      />
    )
  }

  return createPortal(
    <div style={overlay} role="dialog" aria-label={title}>
      <header style={topBar}>
        <Upload size={17} color="var(--color-accent, #1d4ed8)" style={{ flexShrink: 0 }} />
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </h1>
        {(info || review) && <span style={fileChip}>{info?.filename || review?.filename}</span>}
        <div style={{ flex: 1 }} />
        <button onClick={() => finish(null)} style={btn} disabled={!!busy} title="Close (Esc)">
          <X size={14} /> Close
        </button>
      </header>

      <main style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--color-bg-secondary, #f5f4f0)', padding: 16 }}>
        <div style={{ maxWidth: 980, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {error && (
            <div role="alert" style={errorBox}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div>{error.message}</div>
                {error.problems.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {error.problems.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                )}
              </div>
            </div>
          )}

          {step === 'profiles' && (
            <SenderProfiles target={target} transport={transport} senderLabel={senderLabel}
              onBack={() => {
                if (info) setStep('map')
                // Keywords may have been edited: recognise it again.
                else if (review) void recheck({})
                else setStep('pick')
              }} />
          )}

          {step === 'review' && review && detecting && (
            <section style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
              role="status" aria-live="polite">
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
              <div style={{ flex: '1 1 300px' }}>
                <div style={{ fontWeight: 600 }}>Detecting the format and sender…</div>
                <div style={{ color: 'var(--color-text-secondary, #5e6472)', fontSize: 13 }}>
                  {review.text.source === '' || review.text.source === 'transcript'
                    ? 'Reading the scan and looking for who sent it and what it is.'
                    : 'Its keywords did not recognise it, so the AI is looking at the document.'}
                  {' '}This happens once — next time its keywords will.
                </div>
              </div>
              <button type="button" style={btn} onClick={() => void processReview({ generic: true })}>
                Read it with the general instructions instead
              </button>
            </section>
          )}

          {step === 'review' && review && !detecting && (
            <ImportReview review={review} senderLabel={senderLabel}
              busy={busy} onRecheck={(p) => void recheck(p)} onProcess={(b) => void processReview(b)}
              onBack={() => { detectSeq.current++; setStep('pick'); setReview(null); setError(null) }}
              searchSenders={transport.searchSenders ? (q) => transport.searchSenders!(target, q) : undefined}
              onManage={review.can_edit && profilesInfo?.can_manage ? () => setStep('profiles') : undefined} />
          )}

          {step === 'pick' && doneRef.current && doneRef.current.saved > 0 && (
            <div role="status" style={{ ...hintBox, color: '#166534', background: 'rgba(22,101,52,0.08)' }}>
              <span>
                {doneRef.current.saved} record{doneRef.current.saved === 1 ? '' : 's'} imported so far
                in this window. Choose the next file, or Close when you are done.
              </span>
            </div>
          )}

          {step === 'pick' && !locked && canReview && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={detect} disabled={!!busy} style={{ marginTop: 3 }}
                onChange={(e) => changeDetect(e.target.checked)} />
              <span>
                <span style={{ fontWeight: 500 }}>Recognise the sender and document type</span>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary, #5e6472)' }}>
                  {detect
                    ? 'A PDF, photo or Word file is checked against its keywords first, and read with the prompt saved for that sender and type.'
                    : 'Documents are read with this screen’s general instructions — no sender or type is looked for.'}
                  {' '}Remembered for this screen.
                </span>
              </span>
            </label>
          )}

          {step === 'pick' && hasSender && detect && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13,
                color: 'var(--color-text-secondary, #5e6472)', flex: '1 1 260px' }}>
                From (optional) — the {senderLabel.toLowerCase()} who sent it
                <input value={sender} onChange={(e) => setSender(e.target.value)} list="tw-import-senders"
                  placeholder="Recognised from the document when left blank" disabled={!!busy}
                  style={{ ...select, width: '100%' }} aria-label="Sender" />
                <datalist id="tw-import-senders">
                  {senderOptions.map(([code, display]) => (
                    <option key={code} value={code}>{display}</option>
                  ))}
                </datalist>
              </label>
              {profilesInfo?.can_manage && (
                <button type="button" style={btn} disabled={!!busy} onClick={() => setStep('profiles')}>
                  <UserRound size={14} /> Sender profiles…
                </button>
              )}
            </div>
          )}

          {step === 'pick' && locked && (
            <section style={card} aria-label="PDF password">
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                {locked.filename} is password-protected
              </div>
              <div style={{ color: 'var(--color-text-secondary, #5e6472)', marginBottom: 10 }}>
                Enter its password to read it. It is used to open the file and is never shown again.
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="password" value={password} autoComplete="off" aria-label="PDF password"
                  onChange={(e) => setPassword(e.target.value)} disabled={!!busy}
                  onKeyDown={(e) => { if (e.key === 'Enter') void unlock() }}
                  style={{ ...select, flex: '1 1 240px' }} placeholder="Password" />
                <button type="button" style={primaryBtn} disabled={!password || !!busy || !transport.unlock}
                  onClick={() => void unlock()}>
                  {busy ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> {busy}</> : 'Unlock and read'}
                </button>
                <button type="button" style={btn} disabled={!!busy}
                  onClick={() => { setLocked(null); setPassword('') }}>Choose another file</button>
              </div>
              {hasSender && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={rememberPassword}
                    onChange={(e) => setRememberPassword(e.target.checked)} />
                  Remember this password for this {senderLabel.toLowerCase()} (stored encrypted, used automatically next time)
                </label>
              )}
            </section>
          )}

          {step === 'pick' && !locked && (
            <label
              style={{ ...dropZone, borderColor: dragOver ? 'var(--color-accent, #2f5fe3)' : 'var(--color-border, #d6d5cf)' }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                const f = e.dataTransfer.files?.[0]
                if (f && !busy) void upload(f)
              }}
            >
              {busy ? <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} /> : <FileUp size={28} color="var(--color-accent, #1d4ed8)" />}
              <div style={{ fontSize: 16, fontWeight: 600 }}>{busy || 'Choose a file to import'}</div>
              <div style={{ color: 'var(--color-text-secondary, #5e6472)' }}>
                CSV, TSV, Excel (.xlsx / .xls), OpenDocument (.ods) or JSON — or a document to read: a PDF, a photo
                or scan (PNG / JPG / WebP) or Word (.docx), such as a customer&apos;s order. Up to 10 MB. Drop it here or
                click to browse.
              </div>
              <input
                ref={fileRef} type="file" accept={ACCEPT} style={{ display: 'none' }} disabled={!!busy}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) void upload(f)
                }}
              />
            </label>
          )}

          {step === 'map' && info && (
            <>
              <section style={card}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                  <Field label="What to do">
                    {info.lock_mode ? (
                      <div style={{ height: 32, display: 'flex', alignItems: 'center' }}>{MODE_LABELS[mode]}</div>
                    ) : (
                      <select value={mode} onChange={(e) => setMode(e.target.value as ImportMode)} style={select}>
                        {(Object.keys(MODE_LABELS) as ImportMode[]).map((m) => (
                          <option key={m} value={m}>{MODE_LABELS[m]}</option>
                        ))}
                      </select>
                    )}
                  </Field>
                  {info.sheets.length > 1 && (
                    <Field label="Sheet">
                      <select value={info.sheet || ''} onChange={(e) => void changeSheet(e.target.value)} style={select} disabled={!!busy}>
                        {info.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                  )}
                  {childMapped && (
                    <Field label="One record per">
                      <select value={effectiveGroupBy} onChange={(e) => setGroupBy(e.target.value)} style={select}>
                        {headerMapped.map(([, t]) => (
                          <option key={t} value={t}>{optionByValue.get(t)?.label || t}</option>
                        ))}
                      </select>
                    </Field>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32, cursor: 'pointer' }}>
                    <input type="checkbox" checked={monthFirst} onChange={(e) => setMonthFirst(e.target.checked)} />
                    Dates are month-first (MM/DD/YYYY)
                  </label>
                </div>
                <div style={{ marginTop: 10, color: 'var(--color-text-secondary, #5e6472)' }}>
                  {info.rows} row{info.rows === 1 ? '' : 's'} · {info.columns.length} column{info.columns.length === 1 ? '' : 's'}
                  {' · '}{mapped.length} mapped. Columns were matched to fields by name — change any that landed in the wrong place,
                  or choose “Don’t import” to leave a column out.
                  {lookupOptions.length > 0 && <> Lookup fields accept a name or description too; a column such as a
                  description or size can be set to “Helps find …” to sharpen the match.</>}
                  {info.sender?.code && <> <strong>From {info.sender.display || info.sender.code}</strong>
                    {info.sender.how === 'layout' ? ' — their usual layout, so their remembered column map is used.'
                      : info.sender.profile?.has_instructions ? ' — read with their profile’s instructions.' : '.'}</>}
                </div>
                {keyHint && (
                  <div style={{ ...hintBox, marginTop: 10 }}>
                    <KeyRound size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      {keyHint.text}
                      {!keyHint.mappedToKey && headerKey && (
                        <> {' '}
                          <button type="button" style={linkBtn}
                            onClick={() => setMap((m) => {
                              const next: Record<string, string> = {}
                              Object.entries(m).forEach(([k, t]) => { if (t !== headerKey) next[k] = t })
                              next[`#${keyHint.column}`] = headerKey
                              return next
                            })}
                          >Use it as the key</button>
                        </>
                      )}
                    </span>
                  </div>
                )}
                {info.warnings.length > 0 && (
                  <ul style={{ margin: '10px 0 0', paddingLeft: 18, color: '#92400e' }}>
                    {info.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
              </section>

              <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>File column</th>
                      <th style={th}>Sample values</th>
                      <th style={{ ...th, width: 320 }}>Goes into</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info.columns.map((c) => {
                      const key = `#${c.index}`
                      const value = map[key] || ''
                      const dup = value && duplicates.has(value)
                      return (
                        <tr key={key} style={{ opacity: value ? 1 : 0.7 }}>
                          <td style={td}>
                            <div style={{ fontWeight: 500 }}>{c.raw || `Column ${c.index}`}</div>
                            <div style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>
                              {c.filled} filled{c.section ? ` · section ${c.section}` : ''}
                            </div>
                          </td>
                          <td style={{ ...td, color: 'var(--color-text-secondary, #5e6472)', maxWidth: 320 }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.samples.join(' · ') || '—'}
                            </div>
                          </td>
                          <td style={td}>
                            <select
                              value={value}
                              onChange={(e) => setMap((m) => ({ ...m, [key]: e.target.value }))}
                              style={{ ...select, width: '100%', borderColor: dup ? '#dc2626' : undefined }}
                              aria-label={`Target for ${c.raw || `column ${c.index}`}`}
                            >
                              <option value="">Don’t import</option>
                              {groups.map((g) => (
                                <optgroup key={g.group} label={g.group}>
                                  {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </optgroup>
                              ))}
                              {lookupOptions.length > 0 && (
                                <optgroup label="Helps find a record (not imported)">
                                  {lookupOptions.map((o) => (
                                    <option key={`${HINT_PREFIX}${o.value}`} value={`${HINT_PREFIX}${o.value}`}>
                                      Helps find {o.label.replace(/ \(key\)| \*$/g, '')}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                            {dup && <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 3 }}>Another column goes here too.</div>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </section>

              {missingRequired.length > 0 && (
                <div style={hintBox}>
                  <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>
                    Required and not in the file: {missingRequired.join(', ')}. New records will need these filled in the preview.
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <button type="button" style={btn} disabled={!!busy}
                  onClick={() => { setStep('pick'); setInfo(null); setReview(null); setError(null) }}
                >Choose another file</button>
                <button type="button" style={primaryBtn}
                  disabled={!!busy || mapped.length === 0 || duplicates.size > 0}
                  onClick={() => void prepare()}
                >
                  {busy ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> {busy}</> : <>Preview the records <ArrowRight size={14} /></>}
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>,
    document.body,
  )
}

function addOutcomes(a: ImportActionOutcome, b: ImportActionOutcome): ImportActionOutcome {
  return {
    total: a.total + b.total, saved: a.saved + b.saved, failed: a.failed + b.failed,
    pending: a.pending + b.pending, unchanged: (a.unchanged || 0) + (b.unchanged || 0),
    ...((a.leftOut || b.leftOut) ? { leftOut: (a.leftOut || 0) + (b.leftOut || 0) } : {}),
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--color-text-secondary, #5e6472)' }}>
      {label}
      <span style={{ color: 'var(--color-text-primary, #1f2937)', fontSize: 14 }}>{children}</span>
    </label>
  )
}

// ── Styles (matched to ImportPreview) ───────────────────────────────

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9700, display: 'flex', flexDirection: 'column',
  background: 'var(--color-bg-primary, #fff)', color: 'var(--color-text-primary, #1f2937)', fontSize: 14,
}
const topBar: React.CSSProperties = {
  height: 52, flexShrink: 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 12,
  padding: '0 12px 0 18px', borderBottom: '1px solid var(--color-border, #e6e5e0)',
  background: 'var(--color-bg, #fff)',
}
const fileChip: React.CSSProperties = {
  fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 13, padding: '1px 6px',
  borderRadius: 5, background: 'var(--color-bg-secondary, #f1f0eb)', color: 'var(--color-text, #15171c)',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
}
const btn: React.CSSProperties = {
  height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', fontSize: 14,
  fontWeight: 500, borderRadius: 8, border: '1px solid var(--color-border, #e6e5e0)',
  background: 'var(--color-bg, #fff)', color: 'inherit', cursor: 'pointer', flexShrink: 0,
}
const primaryBtn: React.CSSProperties = {
  ...btn, border: '1px solid var(--color-accent, #2f5fe3)', background: 'var(--color-accent, #2f5fe3)', color: '#fff',
}
const linkBtn: React.CSSProperties = {
  border: 0, background: 'transparent', padding: 0, color: 'var(--color-accent, #1d4ed8)',
  cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline',
}
const card: React.CSSProperties = {
  background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #e6e5e0)',
  borderRadius: 12, padding: 14,
}
const dropZone: React.CSSProperties = {
  ...card, borderStyle: 'dashed', borderWidth: 2, minHeight: 260, display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', cursor: 'pointer', padding: 24,
}
const select: React.CSSProperties = {
  height: 32, minWidth: 200, padding: '0 8px', borderRadius: 7, fontSize: 14,
  border: '1px solid var(--color-border, #d6d5cf)', background: 'var(--color-bg, #fff)', color: 'inherit',
}
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', fontSize: 13, fontWeight: 500,
  color: 'var(--color-text-secondary, #5e6472)', background: 'var(--color-bg-secondary, #fafaf8)',
  borderBottom: '1px solid var(--color-border, #e6e5e0)',
}
const td: React.CSSProperties = {
  padding: '8px 12px', borderBottom: '1px solid var(--color-border, #f0efea)', verticalAlign: 'top',
}
const errorBox: React.CSSProperties = {
  display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10, color: '#991b1b',
  background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
}
const hintBox: React.CSSProperties = {
  display: 'flex', gap: 8, padding: '8px 10px', borderRadius: 8, fontSize: 13,
  color: 'var(--color-text, #15171c)', background: 'rgba(47,95,227,0.08)',
}
